// eslint-disable-next-line @nx/enforce-module-boundaries
import { PAGES } from '../../../../shared/client/routes/src/index.js';
import { USER_ROLES as ROOT_USER_ROLES } from '@ptml/root-config';
import { Claim, UserData, AccountType } from './user-types';

/**
 * List of roles available for users logging into RepairerFrontend.
 */
export const USER_ROLES = [
    ...ROOT_USER_ROLES,
    'ownership:mso-group',
    'ownership:mso-corporate',
] as const;

/**
 * Type representing a user role.
 */
export type userRoles = (typeof USER_ROLES)[number];

/**
 * List of roles available for users logging into RepairerAdmin.
 */
export const ADMIN_USER_ROLES = ['PTDeveloper', 'SystemAdmin'] as const;

/**
 * Type representing an admin user role.
 */
export type adminUserRoles = (typeof ADMIN_USER_ROLES)[number];

/**
 * Roles that grant access to settings administration features
 * (Preferred Vendor Management, Multi-Location Access, etc.).
 */
export const SETTINGS_ADMIN_ROLES: ReadonlyArray<userRoles> = [
    'HeadOfficeCompanyAdmin',
    'SupportLead',
    'Support',
] as const;

/**
 * Generates an array of claims applicable to Feature Flag intercept responses.
 * @param roles - Array of repairer roles.
 * @returns Array of Claim objects.
 */
export const getRoleClaims = (roles: Array<userRoles>): Array<Claim> => {
    return roles.map((role) => ({
        type: 'role',
        value: role.toString(),
    }));
};

/**
 * Checks if the user has at least one of the required roles.
 * @param userData - User data containing roles.
 * @param allowedRoles - Array of roles that are allowed.
 * @returns True if the user has one of the allowed roles, false otherwise.
 */
export const hasRequiredRole = (
    userData: UserData,
    allowedRoles: Array<userRoles> = [],
): boolean => {
    const allowedRolesSet = new Set<string>(allowedRoles);
    return userData?.roles?.some((role) => allowedRolesSet.has(role)) ?? false;
};

/**
 * Checks whether the current user is acting on behalf of a Head Office account.
 *
 * @param {UserData} user - User data including impersonation and company type fields.
 * @returns {boolean} True when impersonating and account type is Head Office (4), otherwise false.
 */
export const isActingOnBehalfOfHeadOfficeAccount = (
    user: UserData,
): boolean => {
    const isImpersonating = Boolean(user?.principal_impersonatorid);
    const accountTypeId = Number(user?.principal_accounttypeid);
    return isImpersonating && accountTypeId === AccountType.HeadOffice;
};

/**
 * Determines the homepage URL based on the user's role. Redirects are from the UrlResolutionService.cs in PTUSA.
 *
 * @param {UserData} userData - The data of the user, including their roles.
 * @param {Set<userRoles>} userRoles - A set of user roles to check against.
 * @returns {string} The URL to navigate to based on the user's role.
 */
export const getHomepageUrl = (
    userData: UserData,
    userRoles: Set<string>,
): string => {
    const matchedRoles = new Set(
        userData.roles.filter((role) => userRoles.has(role)),
    );
    const hasSettingsAdminRole =
        matchedRoles.has('SystemAdmin') ||
        SETTINGS_ADMIN_ROLES.some((role) => matchedRoles.has(role));
    if (
        hasSettingsAdminRole &&
        !(
            matchedRoles.has('Estimator') ||
            matchedRoles.has('PartsOrderer') ||
            matchedRoles.has('RemoteEstimator')
        )
    ) {
        return PAGES.settingsPreferredVendors;
    }
    return PAGES.activeJobs;
};
